import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

export default function useReactQuery({
  queryFn,
  staleTime = 1200000,
  enabled = true,
  queryKey,
  showOnSuccessMessage = false,
  onSuccessMessage,
  hideErrorOverlay = false,
  refetchOnWindowFocus = false, // stops refetching when a user focuses on a different window & comes back (bit of a silly default behavior)
  refetchOnMount, // optional: allow callers to override default refetch-on-mount behavior
  onSuccess = () => {},
  onError = () => {},
  nextPrefetch = null,
  select, // optional transform for React Query to apply to the result
  retry,
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  onSuccessMessage = onSuccessMessage ?? t('success', 'Success!')
  const showStatusOverlay = useStatusOverlay()

  const query = useQuery({
    queryKey,
    queryFn,
    staleTime,
    enabled,
    refetchOnWindowFocus,
    refetchOnMount, // pass through when provided
    select, // forward to react-query if provided
    retry,
    onSuccess: (data) => {
      if (showOnSuccessMessage) {
        showStatusOverlay({ message: onSuccessMessage, type: 'success' })
      }
      if (nextPrefetch && 'requestIdleCallback' in window) {
        requestIdleCallback(() => {
          queryClient.prefetchQuery(nextPrefetch.queryKey, nextPrefetch.queryFn)
        })
      }
      onSuccess(data)
    },
    onError: (error) => {
      const errorCode = error?.response?.status
      if (errorCode >= 500) {
        !hideErrorOverlay &&
          showStatusOverlay({
            message: t('error.server', 'The server encountered an error and has been reported. Try again.'),
            type: 'error',
          })
      } else {
        !hideErrorOverlay &&
          showStatusOverlay({ message: t('error.generic', 'Oops. Something went wrong.'), type: 'error' })
        onError(error)
      }
    },
  })
  // If a select function is used, query.data is already the transformed result.
  // Otherwise, our services generally return axios responses, so prefer data.data.
  const selectedOrAxiosData = select ? query?.data : query?.data?.data
  return { query, data: selectedOrAxiosData }
}
